// @owners { team: patients-team }
import { Experimentation } from '@alto/experimentation';
import { useMemo } from 'react';

// Match the feature name with the key in the disabled_features_by_company feature flag
// https://growthbook.prod.alto.com/features/disabled_features_by_company

export type FeatureName =
  | 'app_instructional_videos'
  | 'add_family_member'
  | 'alto_pharmacy_info'
  | 'create_account'
  | 'download_app'
  | 'otc'
  | 'plaid_bank_account'
  | 'push_notification'
  | 'transfer_prescription'
  | 'unboxing_guide';

// Ensure we always return a mapping for all FeatureNames with boolean values
const FEATURE_LIST: FeatureName[] = [
  'app_instructional_videos',
  'add_family_member',
  'alto_pharmacy_info',
  'create_account',
  'download_app',
  'otc',
  'plaid_bank_account',
  'push_notification',
  'transfer_prescription',
  'unboxing_guide',
];

// Use this hook to get the mapping of disabled features for the current company
export const useFeatureDisablingMapping = (): {
  disabledFeatures: Record<FeatureName, boolean>;
  isLoading: boolean;
} => {
  const { value, isLoading } = Experimentation.useFeatureFlag('disabled_features_by_company');

  // If value is undefined or the feature is missing, treat as not disabled (false)
  const disabledFeatures: Record<FeatureName, boolean> = useMemo(
    () =>
      FEATURE_LIST.reduce(
        (acc, feature) => {
          acc[feature] = Boolean((value as Record<string, unknown> | undefined)?.[feature]);
          return acc;
        },
        {} as Record<FeatureName, boolean>,
      ),
    [value],
  );

  return { disabledFeatures, isLoading };
};

// Use this hook to check if a specific feature is disabled for the current company
export const useIsFeatureDisabledForCompany = (
  featureName: FeatureName,
): { isFeatureDisabled: boolean; isLoading: boolean } => {
  const { disabledFeatures, isLoading } = useFeatureDisablingMapping();
  return { isFeatureDisabled: disabledFeatures[featureName] ?? false, isLoading };
};
